import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { userManager } from '../config/auth.config.js'
import { resolveRoles, hasPermission as checkPermission, ROLES, PERMISSIONS } from '../utils/roleResolver.js'
import api from '../services/api.js'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const roles = ref([])
  const permissions = ref([])
  const userProfile = ref(null)
  const isLoading = ref(true) // starts as true until initial load completes
  const isReady = ref(false) // becomes true once initial auth check is done

  const isAuthenticated = computed(() => !!user.value && !user.value.expired)
  const accessToken = computed(() => user.value?.access_token ?? null)
  const userEmail = computed(() => user.value?.profile?.email ?? null)
  const hasRole = (role) => roles.value.includes(role)
  const hasPermissionCheck = (permission) => checkPermission(roles.value, permission)

  // Convenience role checks
  const isAdmin = computed(() => roles.value.includes(ROLES.ADMIN))
  const isProgramManager = computed(() => roles.value.includes(ROLES.PROGRAM_MANAGER))
  const isTeamLead = computed(() => roles.value.includes(ROLES.TEAM_LEAD))
  const canManagePanelists = computed(() => checkPermission(roles.value, PERMISSIONS.PANELIST_MANAGE))
  const canSubmitScores = computed(() => checkPermission(roles.value, PERMISSIONS.SCORE_SUBMIT))
  const canCreateInitiative = computed(() => checkPermission(roles.value, PERMISSIONS.INITIATIVE_CREATE))
  const canCreateStretchProject = computed(() => checkPermission(roles.value, PERMISSIONS.STRETCH_CREATE))
  const canParticipateInStretch = computed(() => checkPermission(roles.value, PERMISSIONS.STRETCH_PARTICIPATE))
  const canViewUsers = computed(() => checkPermission(roles.value, PERMISSIONS.USERS_VIEW))
  const canManageUsers = computed(() => checkPermission(roles.value, PERMISSIONS.USERS_MANAGE))

  /**
   * Fetch the current user's profile, roles, and permissions from the backend.
   * Falls back to local role resolution if the API is unreachable.
   * This call uses skipAuthRedirect to avoid triggering the 401 interceptor loop.
   */
  const fetchUserProfile = async () => {
    try {
      const res = await api.get('/api/v1/users/me', { _skipAuthRedirect: true })
      const data = res.data
      userProfile.value = data
      roles.value = data.roles || [ROLES.CONTRIBUTOR]
      permissions.value = data.permissions || []

      // Hydrate profile picture cache so UserAvatar renders immediately
      if (data.profilePicture && data.email) {
        const { useProfilePicture } = await import('../composables/useProfilePicture.ts')
        const { setCachedPicture } = useProfilePicture()
        setCachedPicture(data.email, data.profilePicture)
      }
    } catch (err) {
      console.warn('[Auth] Failed to fetch user profile from backend, using local fallback:', err?.message)
      // Keep existing locally-resolved roles
    }
  }

  const loadUser = async () => {
    isLoading.value = true
    try {
      const oidcUser = await userManager.getUser()
      user.value = oidcUser
      if (oidcUser?.profile?.email) {
        // Local fallback while backend responds
        roles.value = resolveRoles(oidcUser.profile.email)
        // Then fetch actual roles from backend
        await fetchUserProfile()
      }
    } finally {
      isLoading.value = false
      isReady.value = true
    }
  }

  /**
   * Wait until the auth store has finished its initial load.
   * Used by the router guard to avoid redirecting before we know the auth state.
   */
  const waitUntilReady = () => {
    if (isReady.value) return Promise.resolve()
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (isReady.value) {
          clearInterval(check)
          resolve()
        }
      }, 20)
    })
  }

  const signin = (redirectTo) => {
    if (!import.meta.env.VITE_OKTA_AUTHORITY || !import.meta.env.VITE_OKTA_CLIENT_ID) {
      console.error('[Auth] VITE_OKTA_AUTHORITY and VITE_OKTA_CLIENT_ID must be set. Add them to .env.local for dev.')
      return
    }
    return userManager.signinRedirect({ state: { redirectTo } })
  }

  const signout = async () => {
    // Capture id_token before clearing — needed for Okta to terminate SSO session
    const idToken = user.value?.id_token

    // Clear local state first so router guard won't re-trigger signin
    await userManager.removeUser()
    user.value = null
    roles.value = []
    permissions.value = []
    userProfile.value = null

    // Signal other tabs to clear their auth state
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('auth_logout_signal', Date.now().toString())
      localStorage.removeItem('auth_logout_signal')
    }

    try {
      // RP-Initiated Logout with id_token_hint ensures Okta kills the SSO session
      await userManager.signoutRedirect({ id_token_hint: idToken })
    } catch (err) {
      console.warn(
        '[Auth] Federated sign-out failed, falling back to local redirect.',
        err
      )
      if (typeof window !== 'undefined') {
        window.location.assign('/')
      }
    }
  }

  const handleCallback = async () => {
    const oidcUser = await userManager.signinRedirectCallback()
    user.value = oidcUser
    if (oidcUser?.profile?.email) {
      roles.value = resolveRoles(oidcUser.profile.email)
      // Fetch real roles from backend
      await fetchUserProfile()
    }
    return oidcUser
  }

  // Keep user in sync when silent renew fires or session ends
  userManager.events.addUserLoaded(async (oidcUser) => {
    user.value = oidcUser
    if (oidcUser?.profile?.email) {
      roles.value = resolveRoles(oidcUser.profile.email)
      await fetchUserProfile()
    }
  })

  userManager.events.addUserUnloaded(() => {
    user.value = null
    roles.value = []
    permissions.value = []
    userProfile.value = null
  })

  userManager.events.addSilentRenewError((err) => {
    console.warn('[Auth] Silent renew failed, forcing re-login:', err?.message)
    user.value = null
    roles.value = []
    permissions.value = []
    userProfile.value = null
  })

  userManager.events.addAccessTokenExpired(() => {
    user.value = null
    roles.value = []
    permissions.value = []
    userProfile.value = null
  })

  // Cross-tab logout detection keeps all open tabs in sync when one tab
  // clears the shared OIDC session.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key === 'auth_logout_signal') {
        user.value = null
        roles.value = []
        permissions.value = []
        userProfile.value = null
        userManager.removeUser()
      }
    })
  }

  return {
    user,
    roles,
    permissions,
    userProfile,
    isLoading,
    isReady,
    isAuthenticated,
    accessToken,
    userEmail,
    hasRole,
    hasPermission: hasPermissionCheck,
    // Convenience
    isAdmin,
    isProgramManager,
    isTeamLead,
    canManagePanelists,
    canSubmitScores,
    canCreateInitiative,
    canCreateStretchProject,
    canParticipateInStretch,
    canViewUsers,
    canManageUsers,
    // Actions
    loadUser,
    signin,
    signout,
    handleCallback,
    fetchUserProfile,
    waitUntilReady,
  }
})
